// ============================================
// cloud-save.js — Zero-Knowledge Cloud Save Sync (client core)
// ============================================
//
// Sync-code, zero-knowledge architecture (CLOUD_SAVES.md §2/§3/§5).
//
//   sync code (6 EFF words, generated, never leaves the device unencrypted)
//     ├─ lookup_key = SHA-256("pno-lookup-v1:" + normalized)  → X-Sync-Key
//     └─ enc_key    = PBKDF2-SHA256(normalized, "pno-enc-v1", 100k) → AES-256-GCM
//
//   save JSON ──AES-GCM(iv‖ct)──→ PUT /api/saves/{slot}  (raw bytes)
//
// The server NEVER sees the code, never sees plaintext, holds no identity.
// The derived CryptoKey is NEVER persisted — re-derived per session from the
// phrase. Only the phrase lives in localStorage ('PnO_SyncCode').
//
// CRITICAL project rules honoured here:
//  - NEVER bare `catch {}` — always `catch(e) {}` (web-modules.md; a bare
//    catch kills the whole ES6 module graph silently in Safari).
//  - Singleton with events.createScope('cloud-save') + destroy() lifecycle.
//  - Every public method resolves (never an unhandled rejection); errors land
//    in status().lastError + console.warn.
//  - Debounced pushes use a recursive setTimeout, NEVER nested setInterval.
//  - This module ONLY reads/encrypts/transports save payloads. Applying a
//    pulled save is the UI's job via the existing game-loader path.

import { events, Events } from '../engine/events.js';
import { isPat } from '../engine/pgate.js';
import { fetchWithEdgeRetry } from '../engine/story-fetch.js';
import { EFF_WORDS, EFF_WORDS_SET } from '../data/eff-wordlist.js';
// Acyclic: save.js never imports cloud-save (push trigger goes via events).
import { saveSystem } from './save.js';

// ============================================
// Constants / config
// ============================================

// CLOUD_SAVES_ENABLED kill-switch STRIPPED 2026-07-07 (armed 0.2.8, no
// incidents; killswitch_registry). Feature availability = crypto capability.

// Same origin/host that analytics.js proved works cross-CORS to the VPS.
const API_BASE = 'https://www.pinkedandowned.com/api/saves';

// localStorage keys (CLOUD_SAVES.md §3)
const LS_OPT_IN = 'PnO_CloudSync';   // '1' = enabled
const LS_CODE = 'PnO_SyncCode';      // the phrase (the only persisted secret)
const LS_META = 'PnO_CloudMeta';     // JSON: { "<slot>": { updated_at, size } }

// Crypto contract (pinned, do not improvise)
const LOOKUP_PREFIX = 'pno-lookup-v1:';
const ENC_SALT = 'pno-enc-v1';
const PBKDF2_ITERS = 100000;
const IV_LEN = 12;

// Save-slot storage keys — mirror save.js (SLOT_PREFIX / AUTO_SAVE_KEY).
const SLOT_PREFIX = 'PnO_Save_';
const AUTO_SAVE_KEY = 'PnO_AutoSave';

// Server limits (informational client-side guards; server is the real boundary)
const MAX_BLOB_BYTES = 131072; // 128 KB

// Debounce window for autosave-triggered pushes.
// Autosave fires every turn; pushing each one would blow the server's
// 60-writes/hour budget during active play. 2-minute coalescing keeps the
// steady-state at ~30 pushes/hour with headroom for manual saves, and the
// pagehide flush (init) covers the "quit right after a turn" gap.
const DEBOUNCE_MS = 120000;

const CODE_WORDS = 6;
const WORDLIST_LEN = 7776;
// Rejection-sampling bound: reject Uint16 draws >= this so idx = v % 7776 is
// unbiased (65536 - (65536 % 7776) = 62208).
const REJECT_BOUND = 65536 - (65536 % WORDLIST_LEN);

/* global localforage */

// ============================================
// Pure crypto helpers (exported for the Node test harness)
// ============================================

const _enc = new TextEncoder();
const _dec = new TextDecoder();

/**
 * normalize(phrase): lowercase → split on /[^a-z]+/ → drop empties →
 * join with single "-". Pinned by the crypto contract.
 * @param {string} phrase
 * @returns {string}
 */
export function normalize(phrase) {
    if (typeof phrase !== 'string') return '';
    return phrase
        .toLowerCase()
        .split(/[^a-z]+/)
        .filter(Boolean)
        .join('-');
}

function _toHex(buf) {
    const b = new Uint8Array(buf);
    let out = '';
    for (let i = 0; i < b.length; i++) out += b[i].toString(16).padStart(2, '0');
    return out;
}

/**
 * lookup_key = hex(SHA-256(utf8("pno-lookup-v1:" + normalized))).
 * 64 lowercase hex chars — the X-Sync-Key header value.
 * @param {string} phrase
 * @returns {Promise<string>}
 */
export async function deriveLookupKey(phrase) {
    const normalized = normalize(phrase);
    const data = _enc.encode(LOOKUP_PREFIX + normalized);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return _toHex(digest);
}

/**
 * enc_key = PBKDF2-SHA256(normalized, "pno-enc-v1", 100k, 256) → AES-GCM key.
 * Non-extractable. Re-derived per session — never persisted.
 * @param {string} phrase
 * @returns {Promise<CryptoKey>}
 */
export async function deriveEncKey(phrase) {
    const normalized = normalize(phrase);
    const baseKey = await crypto.subtle.importKey(
        'raw', _enc.encode(normalized), 'PBKDF2', false, ['deriveKey'],
    );
    return crypto.subtle.deriveKey(
        {
            name: 'PBKDF2',
            salt: _enc.encode(ENC_SALT),
            iterations: PBKDF2_ITERS,
            hash: 'SHA-256',
        },
        baseKey,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt'],
    );
}

/**
 * encrypt(saveObj) → Uint8Array(12-byte IV ‖ AES-GCM ciphertext).
 * @param {CryptoKey} encKey
 * @param {object} saveObj
 * @returns {Promise<Uint8Array>}
 */
export async function encryptSave(encKey, saveObj) {
    const iv = crypto.getRandomValues(new Uint8Array(IV_LEN));
    const plaintext = _enc.encode(JSON.stringify(saveObj));
    const ctBuf = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, encKey, plaintext);
    const ct = new Uint8Array(ctBuf);
    const payload = new Uint8Array(IV_LEN + ct.length);
    payload.set(iv, 0);
    payload.set(ct, IV_LEN);
    return payload;
}

/**
 * decrypt(payload) → save object. Throws on GCM auth failure (tampered).
 * @param {CryptoKey} encKey
 * @param {Uint8Array|ArrayBuffer} payload  IV ‖ ciphertext
 * @returns {Promise<object>}
 */
export async function decryptSave(encKey, payload) {
    const bytes = payload instanceof Uint8Array ? payload : new Uint8Array(payload);
    if (bytes.length <= IV_LEN) throw new Error('payload too short');
    const iv = bytes.slice(0, IV_LEN);
    const ct = bytes.slice(IV_LEN);
    const ptBuf = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, encKey, ct);
    return JSON.parse(_dec.decode(ptBuf));
}

/**
 * Generate a 6-word code from the EFF long wordlist using uniform rejection
 * sampling (no modulo bias). Skips the 4 hyphenated EFF words so issued codes
 * always round-trip through normalize()/validatePhrase().
 * @returns {string} space-joined phrase
 */
export function generateCodeWords() {
    const words = [];
    const buf = new Uint16Array(1);
    while (words.length < CODE_WORDS) {
        crypto.getRandomValues(buf);
        const v = buf[0];
        if (v >= REJECT_BOUND) continue; // reject to kill modulo bias
        const word = EFF_WORDS[v % WORDLIST_LEN];
        // Skip hyphenated EFF words (drop-down, felt-tip, t-shirt, yo-yo):
        // normalize() would shatter them, so a code containing one couldn't
        // be re-typed. Treat as a rejected draw.
        if (!/^[a-z]+$/.test(word)) continue;
        words.push(word);
    }
    return words.join(' ');
}

// ============================================
// Singleton manager
// ============================================

class CloudSaveManager {
    constructor() {
        this._scope = events.createScope('cloud-save');
        this._encKey = null;       // derived per session, never persisted
        this._encKeyPhrase = null; // the phrase the cached key was derived from
        this._lookupKey = null;
        this._lookupPhrase = null;
        this._lastSync = null;     // ISO of last successful push/pull
        this._lastError = null;
        this._syncing = false;
        this._debounceTimer = null;
        this._pendingSlots = new Set(); // slots queued by debounced push
        this._inited = false;
    }

    // ---- Availability / opt-in ----

    isAvailable() {
        return typeof crypto !== 'undefined'
            && !!(crypto && crypto.subtle)
            && !!(crypto && crypto.getRandomValues);
    }

    isEnabled() {
        if (!this.isAvailable()) return false;
        try { return localStorage.getItem(LS_OPT_IN) === '1'; }
        catch(e) { return false; }
    }

    /**
     * Opt in. Does NOT generate a code — the settings panel offers an
     * explicit generate-or-adopt choice afterwards, so a second device
     * never creates a throwaway identity before the player can enter
     * their existing code. Idempotent.
     * @returns {Promise<{ok:boolean, code?:string, error?:string}>}
     */
    async enable() {
        try {
            if (!this.isAvailable()) {
                return { ok: false, error: 'crypto_unavailable' };
            }
            try { localStorage.setItem(LS_OPT_IN, '1'); } catch(e) {}
            this._emitStatus();
            return { ok: true, code: this.getCode() };
        } catch(e) {
            this._lastError = e?.message || String(e);
            console.warn('[CloudSave] enable failed:', this._lastError);
            return { ok: false, error: this._lastError };
        }
    }

    /**
     * Opt out. Optionally erases all server copies for this code first.
     * @param {{deleteRemote?:boolean}} opts
     * @returns {Promise<{ok:boolean, deletedRemote?:boolean, error?:string}>}
     */
    async disable({ deleteRemote = false } = {}) {
        let deletedRemote = false;
        try {
            if (deleteRemote && this.getCode()) {
                const res = await this.deleteRemote();
                deletedRemote = !!res?.ok;
            }
        } catch(e) {
            this._lastError = e?.message || String(e);
            console.warn('[CloudSave] disable: deleteRemote failed:', this._lastError);
        }
        try { localStorage.setItem(LS_OPT_IN, '0'); } catch(e) {}
        this._clearDebounce();
        this._emitStatus();
        return { ok: true, deletedRemote };
    }

    // ---- Code management ----

    hasCode() {
        return !!this.getCode();
    }

    getCode() {
        try {
            const c = localStorage.getItem(LS_CODE);
            return c && c.trim() ? c : null;
        } catch(e) { return null; }
    }

    /**
     * Generate + persist a fresh 6-word code.
     * @returns {Promise<string>} the phrase
     */
    async generateCode() {
        const phrase = generateCodeWords();
        try { localStorage.setItem(LS_CODE, phrase); } catch(e) { console.warn('[CloudSave] Failed to persist sync code to localStorage (the load-bearing secret):', e?.message || e); }
        // New code = new identity → drop derived keys + cached meta.
        this._encKey = null;
        this._encKeyPhrase = null;
        this._lookupKey = null;
        this._lookupPhrase = null;
        try { localStorage.removeItem(LS_META); } catch(e) {}
        return phrase;
    }

    /**
     * Validate a phrase against the EFF word set.
     * @param {string} str
     * @returns {{ok:boolean, words:string[], badWords:string[]}}
     */
    validatePhrase(str) {
        const normalized = normalize(str);
        const words = normalized ? normalized.split('-') : [];
        const badWords = words.filter(w => !EFF_WORDS_SET.has(w));
        const ok = words.length === CODE_WORDS && badWords.length === 0;
        return { ok, words, badWords };
    }

    /**
     * Adopt a code typed in from another device: normalize, validate, store,
     * then GET the list to report what already exists for it.
     * @param {string} str
     * @returns {Promise<{ok:boolean, error?:string, foundSaves:Array}>}
     */
    async adoptCode(str) {
        try {
            if (!this.isAvailable()) {
                return { ok: false, error: 'crypto_unavailable', foundSaves: [] };
            }
            const v = this.validatePhrase(str);
            if (!v.ok) {
                return {
                    ok: false,
                    error: v.words.length !== CODE_WORDS ? 'wrong_word_count' : 'unknown_words',
                    foundSaves: [],
                };
            }
            const phrase = v.words.join(' ');
            try { localStorage.setItem(LS_CODE, phrase); } catch(e) { console.warn('[CloudSave] Failed to persist adopted sync code to localStorage (the load-bearing secret):', e?.message || e); }
            // Re-derive on next use.
            this._encKey = null; this._encKeyPhrase = null;
            this._lookupKey = null; this._lookupPhrase = null;
            try { localStorage.removeItem(LS_META); } catch(e) {}

            // Report what exists remotely for this code.
            let foundSaves = [];
            try {
                const list = await this.pullList();
                if (list?.ok) foundSaves = list.saves || [];
            } catch(e) {
                console.warn('[CloudSave] adoptCode: list fetch failed:', e?.message || e);
            }
            this._emitStatus();
            events.emit(Events.CLOUD_SYNC_LIST_REFRESH, { reason: 'adopt' });
            // Owner decision 2026-06-05: entering a code = IMPORT. The cloud
            // versions replace the synced slots (the settings UI shows the
            // warning + confirm BEFORE calling adoptCode). Fire-and-forget;
            // emits its own list refresh when done.
            this.materializeSlots({ overwrite: true }).catch((e) => {});
            // Contract: foundSaves is a COUNT (settings UI renders it directly).
            return { ok: true, foundSaves: foundSaves.length };
        } catch(e) {
            this._lastError = e?.message || String(e);
            console.warn('[CloudSave] adoptCode failed:', this._lastError);
            return { ok: false, error: this._lastError, foundSaves: 0 };
        }
    }

    // ---- Slot policy ----

    /**
     * Slots the CLIENT will sync. Free: autosave + 1 manual. Patreon: all.
     * Server enforces its own quota regardless — this gate is cosmetic.
     * @returns {number[]}
     */
    allowedSlots() {
        try {
            return isPat() ? [-1, 0, 1, 2, 3, 4, 5] : [-1, 0];
        } catch(e) {
            return [-1, 0];
        }
    }

    // ---- Key derivation (cached per phrase, never persisted) ----

    async _getEncKey() {
        const phrase = this.getCode();
        if (!phrase) throw new Error('no_code');
        if (this._encKey && this._encKeyPhrase === phrase) return this._encKey;
        this._encKey = await deriveEncKey(phrase);
        this._encKeyPhrase = phrase;
        return this._encKey;
    }

    async _getLookupKey() {
        const phrase = this.getCode();
        if (!phrase) throw new Error('no_code');
        if (this._lookupKey && this._lookupPhrase === phrase) return this._lookupKey;
        this._lookupKey = await deriveLookupKey(phrase);
        this._lookupPhrase = phrase;
        return this._lookupKey;
    }

    // ---- Local save read (no rebuild; mirror save.js dual-read) ----

    _slotStorageKey(slot) {
        return slot === -1 ? AUTO_SAVE_KEY : `${SLOT_PREFIX}${slot}`;
    }

    /** Parse a save's "YYYY-MM-DD HH:MM" device timestamp to ms (NaN-safe). */
    _saveTsMs(save) {
        try { return Date.parse(String(save?.timestamp || '').replace(' ', 'T')) || 0; }
        catch(e) { return 0; }
    }

    /**
     * Materialize cloud copies into local slots (Steam-Cloud feel).
     * Boot path (default): NEWEST WINS per slot — empty slots are filled, and
     * a local save is replaced only when the cloud copy is strictly newer
     * (>60s margin against cross-device clock skew), so play continues where
     * the OTHER device left off while offline progress is never clobbered by
     * an older cloud state. With {overwrite:true} (code-import path, after
     * the explicit UI warning) cloud versions replace the synced slots
     * unconditionally. Slots without a cloud copy are left untouched.
     * Writes use the same dual-write shape the save system persists
     * (raw save object to IDB + JSON mirror to LS), NO game state is loaded.
     * @param {{overwrite?:boolean}} opts
     * @returns {Promise<{ok:boolean, filled:number[]}>}
     */
    async materializeSlots({ overwrite = false } = {}) {
        const filled = [];
        try {
            if (!this.isEnabled() || !this.hasCode()) return { ok: false, filled };
            const list = await this.pullList();
            const saves = (list && list.ok && Array.isArray(list.saves)) ? list.saves : [];
            const allowed = this.allowedSlots();
            const knownMeta = this._readMeta();
            for (const entry of saves) {
                const slot = entry.slot;
                if (!allowed.includes(slot)) continue;
                let local = null;
                if (!overwrite) {
                    try { local = await this._readLocalSave(slot); } catch(e) {}
                    // Cheap pre-filter: if the remote copy is exactly the one
                    // we last pushed/pulled ourselves, there is nothing new.
                    const known = knownMeta[String(slot)];
                    if (local && known && known.updated_at === entry.updated_at) continue;
                }
                const res = await this.pullSlot(slot);
                if (!res || !res.ok || !res.save) continue;
                if (!overwrite && local) {
                    // Newest wins: only replace when the cloud save is
                    // STRICTLY newer than the local one (60s skew margin).
                    const cloudMs = this._saveTsMs(res.save);
                    const localMs = this._saveTsMs(local);
                    if (!(cloudMs > localMs + 60000)) {
                        // Local is newer/equal — remember the remote version so
                        // we don't re-download it on every boot.
                        this._setSlotMeta(slot, entry.updated_at, entry.size);
                        continue;
                    }
                }
                const key = this._slotStorageKey(slot);
                try {
                    if (typeof localforage !== 'undefined') await localforage.setItem(key, res.save);
                } catch(e) { console.warn('[CloudSave] materialize IDB write failed:', e?.message || e); }
                try { localStorage.setItem(key, JSON.stringify(res.save)); } catch(e) {}
                // Keep the save system's in-memory meta cache in sync, or the
                // slot rows keep rendering the pre-import state ("Empty"/old day).
                try { saveSystem.refreshSlotMetaFromData(slot, res.save); } catch(e) {}
                this._setSlotMeta(slot, entry.updated_at, entry.size);
                filled.push(slot);
            }
            if (filled.length) {
                events.emit(Events.CLOUD_SYNC_LIST_REFRESH, { reason: 'materialize', filled });
            }
            return { ok: true, filled };
        } catch(e) {
            console.warn('[CloudSave] materializeSlots failed:', e?.message || e);
            return { ok: false, filled };
        }
    }

    /**
     * Read the raw stored save object for a slot — the SAME payload the
     * existing system persisted. Dual-read (IDB → LS fallback, newer wins),
     * exactly like save-load-ui.exportSave / saveSystem.loadAutoSave. No
     * migration, no events, no rebuild.
     * @param {number} slot  -1 autosave, 0..5 manual
     * @returns {Promise<object|null>}
     */
    async _readLocalSave(slot) {
        const key = this._slotStorageKey(slot);
        let data = null;
        try {
            if (typeof localforage !== 'undefined') data = await localforage.getItem(key);
        } catch(e) {
            console.warn(`[CloudSave] IDB read failed for ${key}:`, e?.message || e);
        }
        try {
            const raw = localStorage.getItem(key);
            if (raw) {
                const lsData = JSON.parse(raw);
                if (!data) {
                    data = lsData;
                } else if (lsData?.timestamp && lsData.timestamp > (data.timestamp || '')) {
                    data = lsData;
                }
            }
        } catch(e) { /* LS read/parse failed — keep IDB copy */ }
        return data || null;
    }

    // ---- Meta tracking (per-slot last-known server updated_at) ----

    _readMeta() {
        try {
            const raw = localStorage.getItem(LS_META);
            if (!raw) return {};
            const obj = JSON.parse(raw);
            return (obj && typeof obj === 'object') ? obj : {};
        } catch(e) { return {}; }
    }

    _writeMeta(meta) {
        try { localStorage.setItem(LS_META, JSON.stringify(meta)); } catch(e) {}
    }

    _setSlotMeta(slot, updatedAt, size) {
        const meta = this._readMeta();
        meta[String(slot)] = { updated_at: updatedAt, size };
        this._writeMeta(meta);
    }

    // ---- Network primitives ----

    async _fetchWithKey(url, opts = {}) {
        const lookupKey = await this._getLookupKey();
        const headers = Object.assign({}, opts.headers || {}, { 'X-Sync-Key': lookupKey });
        // fetchWithEdgeRetry only forwards `cache`; do a direct fetch for
        // methods/bodies/headers, wrapped in the same retry shape (edge 403
        // class). We reimplement the tiny retry loop here so PUT/DELETE keep
        // their method + body, which fetchWithEdgeRetry can't carry.
        const attempts = opts.attempts || 3;
        const backoff = [400, 1200];
        let lastErr = null;
        for (let attempt = 1; attempt <= attempts; attempt++) {
            try {
                const resp = await fetch(url, {
                    method: opts.method || 'GET',
                    headers,
                    body: opts.body,
                    cache: 'no-store',
                    keepalive: !!opts.keepalive,
                });
                return resp; // caller inspects resp.ok / resp.status
            } catch(e) {
                lastErr = e;
                console.warn(`[CloudSave] ${opts.method || 'GET'} ${url} attempt ${attempt}/${attempts} failed: ${e?.message || e}`);
                if (attempt < attempts) {
                    await new Promise(r => setTimeout(r, backoff[attempt - 1] || 1200));
                }
            }
        }
        throw lastErr || new Error('network_error');
    }

    // ---- Push (encrypt + PUT) ----

    /**
     * Encrypt + upload one slot. Skips slots not in allowedSlots() and empty
     * slots. Resolves with a status object (never rejects).
     * @param {number} slot
     * @returns {Promise<{ok:boolean, skipped?:string, error?:string, updated_at?:string, size?:number}>}
     */
    async syncSlot(slot, opts = {}) {
        try {
            if (!this.isEnabled()) return { ok: false, skipped: 'disabled' };
            if (!this.hasCode()) return { ok: false, skipped: 'no_code' };
            if (!this.allowedSlots().includes(slot)) return { ok: false, skipped: 'slot_not_allowed' };

            const saveObj = await this._readLocalSave(slot);
            if (!saveObj) return { ok: false, skipped: 'empty' };

            const encKey = await this._getEncKey();
            const payload = await encryptSave(encKey, saveObj);

            if (payload.length > MAX_BLOB_BYTES) {
                this._lastError = `slot ${slot} too large (${payload.length} bytes)`;
                console.warn('[CloudSave]', this._lastError);
                return { ok: false, error: 'too_large', size: payload.length };
            }

            this._syncing = true;
            this._emitStatus();
            const resp = await this._fetchWithKey(`${API_BASE}/${slot}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/octet-stream' },
                // Send the underlying ArrayBuffer slice so no extra copy/encoding.
                body: payload.buffer.slice(payload.byteOffset, payload.byteOffset + payload.byteLength),
                // pagehide flush: keepalive survives teardown; no time to retry.
                keepalive: !!opts.keepalive,
                attempts: opts.keepalive ? 1 : undefined,
            });
            this._syncing = false;

            if (!resp.ok) {
                let errBody = '';
                try { errBody = (await resp.json())?.error || ''; } catch(e) {}
                this._lastError = `PUT slot ${slot}: HTTP ${resp.status}${errBody ? ' ' + errBody : ''}`;
                console.warn('[CloudSave]', this._lastError);
                this._emitStatus();
                return { ok: false, error: errBody || `http_${resp.status}`, status: resp.status };
            }

            let body = {};
            try { body = await resp.json(); } catch(e) {}
            const updatedAt = body?.updated_at || new Date().toISOString();
            const size = body?.size ?? payload.length;
            this._setSlotMeta(slot, updatedAt, size);
            this._lastSync = updatedAt;
            this._lastError = null;
            this._emitStatus();
            events.emit(Events.CLOUD_SYNC_LIST_REFRESH, { slot });
            return { ok: true, updated_at: updatedAt, size };
        } catch(e) {
            this._syncing = false;
            this._lastError = e?.message || String(e);
            console.warn(`[CloudSave] syncSlot(${slot}) failed:`, this._lastError);
            this._emitStatus();
            return { ok: false, error: this._lastError };
        }
    }

    /**
     * Push every allowed, non-empty slot. Resolves with per-slot results.
     * @returns {Promise<{ok:boolean, results:object}>}
     */
    async syncAll() {
        const results = {};
        try {
            if (!this.isEnabled()) return { ok: false, results, skipped: 'disabled' };
            for (const slot of this.allowedSlots()) {
                results[slot] = await this.syncSlot(slot);
            }
            const anyOk = Object.values(results).some(r => r?.ok);
            return { ok: anyOk, results };
        } catch(e) {
            this._lastError = e?.message || String(e);
            console.warn('[CloudSave] syncAll failed:', this._lastError);
            return { ok: false, results, error: this._lastError };
        }
    }

    // ---- Debounced push (recursive setTimeout, NEVER setInterval) ----

    _queuePush(slot) {
        if (!this.isEnabled() || !this.hasCode()) return;
        if (this.allowedSlots().includes(slot)) this._pendingSlots.add(slot);
        if (this._debounceTimer) return; // coalesce
        this._debounceTimer = setTimeout(() => this._flushDebounced(), DEBOUNCE_MS);
    }

    async _flushDebounced() {
        this._debounceTimer = null;
        const slots = Array.from(this._pendingSlots);
        this._pendingSlots.clear();
        for (const slot of slots) {
            // Never let one slot's failure reject the timer chain.
            try { await this.syncSlot(slot); } catch(e) {}
        }
    }

    _clearDebounce() {
        if (this._debounceTimer) {
            clearTimeout(this._debounceTimer);
            this._debounceTimer = null;
        }
        this._pendingSlots.clear();
    }

    // ---- Pull (GET; decrypt for slot fetch) ----

    /**
     * List remote slots (meta only, no blobs).
     * @returns {Promise<{ok:boolean, saves:Array, error?:string}>}
     */
    async pullList() {
        try {
            if (!this.hasCode()) return { ok: false, saves: [], error: 'no_code' };
            const resp = await this._fetchWithKey(API_BASE, { method: 'GET' });
            if (!resp.ok) {
                this._lastError = `GET list: HTTP ${resp.status}`;
                console.warn('[CloudSave]', this._lastError);
                return { ok: false, saves: [], error: `http_${resp.status}` };
            }
            const body = await resp.json();
            this._lastError = null;
            return { ok: true, saves: body?.saves || [] };
        } catch(e) {
            this._lastError = e?.message || String(e);
            console.warn('[CloudSave] pullList failed:', this._lastError);
            return { ok: false, saves: [], error: this._lastError };
        }
    }

    /**
     * Fetch + decrypt one slot. DOES NOT apply it — applying is the UI's job
     * via the existing game-loader path. Updates per-slot meta on success.
     * @param {number} slot
     * @returns {Promise<{ok:boolean, save?:object, error?:string}>}
     */
    async pullSlot(slot) {
        try {
            if (!this.hasCode()) return { ok: false, error: 'no_code' };
            const resp = await this._fetchWithKey(`${API_BASE}/${slot}`, { method: 'GET' });
            if (resp.status === 404) {
                return { ok: false, error: 'not_found' };
            }
            if (!resp.ok) {
                this._lastError = `GET slot ${slot}: HTTP ${resp.status}`;
                console.warn('[CloudSave]', this._lastError);
                return { ok: false, error: `http_${resp.status}` };
            }
            const buf = await resp.arrayBuffer();
            const encKey = await this._getEncKey();
            const save = await decryptSave(encKey, buf);
            // Track the server timestamp from list-meta cheaply: we don't get
            // updated_at on the raw blob GET, so record size only here; the
            // list endpoint owns updated_at.
            const meta = this._readMeta();
            meta[String(slot)] = Object.assign({}, meta[String(slot)], { size: buf.byteLength });
            this._writeMeta(meta);
            this._lastSync = new Date().toISOString();
            this._lastError = null;
            return { ok: true, save };
        } catch(e) {
            this._lastError = e?.message || String(e);
            console.warn(`[CloudSave] pullSlot(${slot}) failed:`, this._lastError);
            return { ok: false, error: this._lastError };
        }
    }

    // ---- Erasure (Art. 17) ----

    /**
     * Delete ALL server copies for this code (one call).
     * @returns {Promise<{ok:boolean, deleted?:number, error?:string}>}
     */
    async deleteRemote() {
        try {
            if (!this.hasCode()) return { ok: false, error: 'no_code' };
            const resp = await this._fetchWithKey(API_BASE, { method: 'DELETE' });
            if (!resp.ok) {
                this._lastError = `DELETE all: HTTP ${resp.status}`;
                console.warn('[CloudSave]', this._lastError);
                return { ok: false, error: `http_${resp.status}` };
            }
            let body = {};
            try { body = await resp.json(); } catch(e) {}
            try { localStorage.removeItem(LS_META); } catch(e) {}
            this._lastError = null;
            this._emitStatus();
            events.emit(Events.CLOUD_SYNC_LIST_REFRESH, { reason: 'delete' });
            return { ok: true, deleted: body?.deleted ?? 0 };
        } catch(e) {
            this._lastError = e?.message || String(e);
            console.warn('[CloudSave] deleteRemote failed:', this._lastError);
            return { ok: false, error: this._lastError };
        }
    }

    /**
     * Delete one remote slot.
     * @param {number} slot
     * @returns {Promise<{ok:boolean, deleted?:number, error?:string}>}
     */
    async deleteRemoteSlot(slot) {
        try {
            if (!this.hasCode()) return { ok: false, error: 'no_code' };
            const resp = await this._fetchWithKey(`${API_BASE}/${slot}`, { method: 'DELETE' });
            if (!resp.ok) {
                this._lastError = `DELETE slot ${slot}: HTTP ${resp.status}`;
                console.warn('[CloudSave]', this._lastError);
                return { ok: false, error: `http_${resp.status}` };
            }
            let body = {};
            try { body = await resp.json(); } catch(e) {}
            const meta = this._readMeta();
            delete meta[String(slot)];
            this._writeMeta(meta);
            this._lastError = null;
            this._emitStatus();
            events.emit(Events.CLOUD_SYNC_LIST_REFRESH, { slot, reason: 'delete' });
            return { ok: true, deleted: body?.deleted ?? 0 };
        } catch(e) {
            this._lastError = e?.message || String(e);
            console.warn(`[CloudSave] deleteRemoteSlot(${slot}) failed:`, this._lastError);
            return { ok: false, error: this._lastError };
        }
    }

    // ---- Status ----

    /** Broadcast the current status() snapshot (settings UI live-updates). */
    _emitStatus() {
        try { events.emit(Events.CLOUD_SYNC_STATUS, this.status()); } catch(e) {}
    }

    status() {
        return {
            available: this.isAvailable(),
            enabled: this.isEnabled(),
            hasCode: this.hasCode(),
            lastSync: this._lastSync,
            lastError: this._lastError,
            syncing: this._syncing,
        };
    }

    /** Cached per-slot server meta (for cheap remote-newer detection in UI). */
    getSlotMeta() {
        return this._readMeta();
    }

    // ---- Lifecycle ----

    /**
     * Wire the push trigger. Subscribes to GAME_SAVED via the scope.
     * Idempotent — safe to call once from main.js.
     */
    init() {
        if (this._inited) return;
        this._inited = true;
        // Last-chance flush when the tab/app goes away: pending debounced
        // slots are pushed with keepalive fetches (survive page teardown for
        // bodies < 64 KB — our payloads are ~20 KB). Local saves are already
        // safe; this only narrows the cloud-staleness window on quit.
        this._onPageHide = () => {
            try {
                if (!this._pendingSlots || this._pendingSlots.size === 0) return;
                const slots = Array.from(this._pendingSlots);
                this._pendingSlots.clear();
                this._clearDebounce();
                for (const slot of slots) {
                    this.syncSlot(slot, { keepalive: true }).catch((e) => {});
                }
            } catch(e) {}
        };
        window.addEventListener('pagehide', this._onPageHide);
        // The save system emits Events.GAME_SAVED ('cloud:gameSaved') after a
        // successful manual save AND autosave. Manual saves push immediately;
        // autosave-triggered pushes coalesce via the 2-minute debounce.
        this._scope.on(Events.GAME_SAVED, (data) => {
            try {
                if (!this.isEnabled() || !this.hasCode()) return;
                const slot = data?.slot;
                if (slot === undefined || slot === null) return;
                if (!this.allowedSlots().includes(slot)) return;
                if (data?.isAuto) {
                    this._queuePush(slot);     // debounced
                } else {
                    // Manual save → immediate (fire-and-forget, never rejects).
                    this.syncSlot(slot).catch(e => {
                        console.warn('[CloudSave] immediate push failed:', e?.message || e);
                    });
                }
            } catch(e) {
                console.warn('[CloudSave] GAME_SAVED handler error:', e?.message || e);
            }
        });
    }

    destroy() {
        this._clearDebounce();
        if (this._onPageHide) {
            window.removeEventListener('pagehide', this._onPageHide);
            this._onPageHide = null;
        }
        this._scope.destroy();
        this._encKey = null;
        this._encKeyPhrase = null;
        this._lookupKey = null;
        this._lookupPhrase = null;
        this._inited = false;
    }
}

export const cloudSaveManager = new CloudSaveManager();
